/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminLogin.module.css';
import BrandLogo from './BrandLogo';
import config, { logger } from '../config';
import { getApiHeaders } from '../utils/api';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.ADMIN_LOGIN}`, {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem(config.STORAGE_KEYS.TOKEN, data.data.token);
        localStorage.setItem(config.STORAGE_KEYS.NAME, data.data.fullName);
        if (data.data.role) {
          localStorage.setItem(config.STORAGE_KEYS.ADMIN_ROLE, data.data.role);
        } else {
          localStorage.removeItem(config.STORAGE_KEYS.ADMIN_ROLE);
        }

        if (data.data.email) {
          localStorage.setItem('adminEmail', data.data.email);
        }

        navigate('/admin-dashboard');
      } else {
        let errorMessage = data.message || 'Admin login failed. Please try again.';

        if (data.validation_errors && Array.isArray(data.validation_errors)) {
          errorMessage = data.validation_errors.join('\n');
        }

        setError(errorMessage);
      }
    } catch (err) {
      logger.error('Admin login error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <main className={styles['admin-login-container']} id="admin-login-main" aria-label="Admin login">
      <div className={styles['admin-login-content']}>
        <div className={styles['admin-login-form-container']}>
          <BrandLogo alt="Richuel Logo" className={styles['admin-login-logo']} />

          <h1 className={styles['admin-login-title']}>Admin Login</h1>

          {error && (
            <div
              key={error}
              id="admin-login-error"
              className={styles['error-message']}
              role="alert"
              aria-live="polite"
            >
              {error.split('\n').map((errorLine, index) => (
                <div key={index}>{errorLine}</div>
              ))}
            </div>
          )}

          <form className={styles['admin-login-form']} onSubmit={handleSubmit} aria-label="Admin login with email">
            <div className={styles['form-group']}>
              <label htmlFor="admin-email">Email</label>
              <input
                type="email"
                id="admin-email"
                name="email"
                placeholder="Enter admin email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                autoComplete="username"
                aria-describedby={error ? 'admin-login-error' : undefined}
              />
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="admin-password">Password</label>
              <div className={styles['password-input-container']}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="admin-password"
                  name="password"
                  placeholder="Enter admin password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  aria-describedby={error ? 'admin-login-error' : undefined}
                />
                <button
                  type="button"
                  className={styles['password-toggle']}
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                    {showPassword ? (
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    ) : (
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={styles['admin-login-button']}
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default AdminLogin;
